import React, { useState, useEffect,useRef } from 'react';
import {
    StyleSheet,
    View,
    KeyboardAvoidingView,
    Dimensions,
    ScrollView,
    SafeAreaView,
    Animated,
    Platform
} from 'react-native';
import { useSelector, useDispatch } from 'react-redux';

import TouchableItem from '../../../components/TouchableItem';
import Colors from '../../../constants/Colors';
import DefaultInputMaskedMoney from '../../../components/DefaultInputMaskedMoney';
import DefaultText from '../../../components/DefaultText';
import NavContainer from '../../../components/NavContainer';
import DismissKeyboard from '../../../components/DismissKeyboard';

import * as potentialActions from '../../../store/actions/potential';

const InvestmentIncomeScreen = props => {
    const investmentIncomeData = useSelector(state => state.potential.investmentIncome);
    const investmentIncomeAmountData = useSelector(state => state.potential.investmentIncomeAmount);
    const [investmentIncome, setInvestmentIncome] = useState(investmentIncomeData);
    const [investmentIncomeAmount, setInvestmentIncomeAmount] = useState(investmentIncomeAmountData);
    const [investmentIncomeAmountValid, setInvestmentIncomeAmountValid] = useState(investmentIncomeAmountData ? true : false);

    const fadeAnim = useRef(new Animated.Value(0)).current;

    const fadeIn = () => {
        // Will change fadeAnim value to 1 in 5 seconds
        Animated.timing(fadeAnim, {
            toValue: 1,
            duration: 1000,
            useNativeDriver: true
        }).start();
    };

    const fadeOut = () => {
        // Will change fadeAnim value to 0 in 5 seconds
        Animated.timing(fadeAnim, {
            toValue: 0,
            duration: 1000,
            useNativeDriver: true
        }).start();
    };

    useEffect(() => {
        if (investmentIncome === true) {
            fadeIn();
        } else {
            fadeOut();
        }
    }, [investmentIncome])

    const dispatch = useDispatch();

    const inputChangeHandler = async (id, value, valid) => {
        setInvestmentIncomeAmount(value);
        setInvestmentIncomeAmountValid(valid);
        dispatch(potentialActions.setInvestmentIncomeAmount(value));
    };

    const selectionHandler = async (answer) => {
        setInvestmentIncome(answer);
        dispatch(potentialActions.setInvestmentIncome(answer));
        if (investmentIncomeData === '') {
            if (answer === false) {
                props.navigation.navigate('OtherIncome');
            }
        }
    };

    const doneHandler = () => {
        if ( investmentIncomeAmount > 2500 && investmentIncome === true) {
            props.navigation.navigate('Result');
        } else {
            props.navigation.navigate('OtherIncome');
        }
    };

    const sView = useRef();

    useEffect(() => {
        sView.current.scrollToEnd({ animated: true });
    });

    return (
        <SafeAreaView style={styles.screen}>
        <ScrollView ref={sView}>
            <KeyboardAvoidingView behavior='position'>
                <DismissKeyboard>
                        <View style={styles.questionContainer}>
                    <View style={styles.questionTextContainer}>
                        <DefaultText style={styles.questionText}>Did you receive any investment incomes during 2020?</DefaultText>
                        <DefaultText style={styles.note}>These are dividends or interests from securities investments or bank accounts. This does NOT include capital gains.</DefaultText>
                    </View>
                    <View style={styles.answerContainer}>
                        <View style={styles.yesNoContainer}>
                            <TouchableItem
                                onSelectItem={() => selectionHandler(false)}
                                style={investmentIncome === false ? styles.activeAnswer : styles.passiveAnswer}
                            >
                                <DefaultText
                                    style={investmentIncome === false ? styles.activeAnswerText : styles.passiveAnswerText}
                                >
                                    No
                                </DefaultText>
                            </TouchableItem>
                            <TouchableItem
                                onSelectItem={() => selectionHandler(true)}
                                style={investmentIncome === true ? styles.activeAnswer : styles.passiveAnswer}
                            >
                                <DefaultText
                                    style={investmentIncome === true ? styles.activeAnswerText : styles.passiveAnswerText}
                                >
                                    Yes
                            </DefaultText>
                            </TouchableItem>
                        </View>
                        
                        {investmentIncome === true && (
                            <Animated.View 
                            style={[
                                styles.inputContainer,
                                {
                                  opacity: fadeAnim // Bind opacity to animated value
                                }
                              ]}>
                                <DefaultInputMaskedMoney
                                    focus={!(investmentIncomeAmountData > 0)}
                                    id='investmentIncome'
                                    title="Investment income received:"
                                    errorMessage="Please enter a valid amount"
                                    placeHolder="1'000"
                                    keyboardType='numeric'
                                    onInputChange={inputChangeHandler}
                                    initialValue={investmentIncomeAmount}
                                    initiallyValid={investmentIncomeAmountValid}
                                    decimal
                                    required
                                />
                            </Animated.View>
                        )}
                    </View>

                    <NavContainer
                                backShow={true}
                                nextShow={(investmentIncome === false || investmentIncomeAmount > 0)}
                                nextNav={doneHandler}
                                backNav={() => { props.navigation.goBack() }}
                            />

                    </View>
                </DismissKeyboard>
            </KeyboardAvoidingView>
        </ScrollView >
        </SafeAreaView>
    );
};

export const investmentIncomeScreenOptions = navigationData => {
    return {
        title: "Investment Income",
        headerLeft: () =>
            null
    }
};

const styles = StyleSheet.create({
    screen: {
        flex: 1,
        backgroundColor: Colors.backColor,
        padding: 10,
        alignItems: Platform.OS === 'web' ? 'center' : 'stretch',
    },
    questionContainer: {        
        padding: 10,
        minHeight: Dimensions.get('window').height * 0.87,
        justifyContent: Platform.OS === 'web' ? 'center' : 'flex-end',
        maxWidth: 600,
        minWidth: (Platform.OS === 'web' && Dimensions.get('window').width > 600) ? 600 : 0,
    },
    inputContainer: {
        flex: 1,
        marginTop: 20
    },
    yesNoContainer: {
        justifyContent: 'space-between',
        flexDirection: 'row',
    },
    note: {
        paddingHorizontal: 10,
        marginBottom: 2,
        fontFamily: 'lato'
    },
    questionText: {
        fontFamily: 'lato-bold',
        color: Colors.darkBlue,
        fontSize: 20,
        marginBottom: 10,
        paddingHorizontal: 10
    },
    questionTextContainer: {
        marginBottom: 40,
    },
    answerContainer: {
        justifyContent: 'space-between',
        // minHeight: 200,
        padding: 10,
    },
    passiveAnswer: {
        borderRadius: 5,
        borderWidth: 0.5,
        borderColor: Colors.darkGreen,
        minHeight: 50,
        width: (Platform.OS === 'web' && Dimensions.get('window').width > 600) ? 250 : (Dimensions.get('window').width - 100) / 2,
        alignItems: 'center',
        justifyContent: 'center'
    },
    activeAnswer: {
        borderRadius: 5,
        borderWidth: 0.5,
        borderColor: Colors.darkGreen,
        backgroundColor: Colors.buttonGreen,
        minHeight: 50,
        width: (Platform.OS === 'web' && Dimensions.get('window').width > 600) ? 250 : (Dimensions.get('window').width - 100) / 2,
        alignItems: 'center',
        justifyContent: 'center'
    },
    activeAnswerText: {
        fontFamily: 'lato-bold',
        fontSize: 20,
        color: Colors.darkGreen,
    },
    passiveAnswerText: {
        fontFamily: 'lato-bold',
        fontSize: 16,
        color: Colors.darkGreen,
    },
});

export default InvestmentIncomeScreen;